import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatUnreadSummary, ChatUser } from '@nexops/shared';
import { chatApi, chatKeys } from '../api/chat.api';
import { clearConversationUnread, findConversation } from '../api/chat.cache';
import { useChatStore } from './useChatStore';
import { useDebounced } from './useDebounced';

/** Lista rozmów (aktywnych albo z archiwum); strumień SSE aktualizuje cache na bieżąco. */
export function useConversationsQuery(archived: boolean, enabled = true) {
  return useQuery({
    queryKey: chatKeys.conversations(archived),
    queryFn: () => chatApi.conversations(archived),
    enabled,
    staleTime: 60_000,
    meta: { silent: true },
  });
}

/** Szczegóły jednej rozmowy; do czasu odpowiedzi pokazuje wiersz z listy (placeholder). */
export function useConversation(id: string | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: chatKeys.conversation(id ?? ''),
    queryFn: () => chatApi.conversation(id!),
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
    placeholderData: () => (id ? findConversation(qc, id) : undefined),
    meta: { silent: true },
  });
}

/** Podsumowanie nieprzeczytanych (licznik w menu). Gdy strumień nie działa — odpytywanie co 60 s. */
export function useChatUnread(enabled = true) {
  const connected = useChatStore((s) => s.connected);
  return useQuery<ChatUnreadSummary>({
    queryKey: chatKeys.unread,
    queryFn: chatApi.unread,
    enabled,
    staleTime: 30_000,
    refetchInterval: connected ? false : 60_000,
    retry: false,
    meta: { silent: true },
  });
}

/** Osoby, do których można napisać (wyszukiwanie z opóźnieniem). */
export function useContacts(search: string, enabled = true) {
  const debounced = useDebounced(search.trim(), 250);
  return useQuery<ChatUser[]>({
    queryKey: chatKeys.contacts(debounced),
    queryFn: () => chatApi.contacts(debounced),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/** Oznacza rozmowę jako przeczytaną (licznik zerowany od razu, serwer zwraca nowe podsumowanie). */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => chatApi.markRead(id),
    onMutate: (id) => clearConversationUnread(qc, id),
    onSuccess: (summary) => {
      if (summary && typeof summary.total === 'number') qc.setQueryData(chatKeys.unread, summary);
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: chatKeys.all });
    },
    meta: { silent: true },
  });
}
